import { useState, useCallback } from 'react';
import { 
    validateForDefaultPCSafety, 
    logDefaultPCSafetyViolation,
    DefaultPCSafetyBlockResult
} from '../utils/defaultPCSafety';

export interface SafetyWarningState {
    isVisible: boolean;
    message: string;
    type: 'error' | 'warning';
    operationType: 'R2F' | 'GSLA';
}

export interface UseDefaultPCSafetyProps {
    operationType: 'R2F' | 'GSLA';
    userAlias?: string;
    warehouseId?: string;
}

export interface UseDefaultPCSafetyReturn {
    warningState: SafetyWarningState | null;
    validateSafety: (changes: any[]) => DefaultPCSafetyBlockResult;
    showWarning: (message: string, type?: 'error' | 'warning') => void;
    dismissWarning: () => void;
    logViolation: (defaultPCCount: number) => void;
}

/**
 * Consolidated hook for Default_PC safety validation and warning management
 * Used by both R2F and GSLA operations to ensure consistent safety behavior
 */
export function useDefaultPCSafety({
    operationType,
    userAlias = 'unknown',
    warehouseId
}: UseDefaultPCSafetyProps): UseDefaultPCSafetyReturn {
    const [warningState, setWarningState] = useState<SafetyWarningState | null>(null);

    const validateSafety = useCallback((changes: any[]): DefaultPCSafetyBlockResult => {
        return validateForDefaultPCSafety(changes, operationType);
    }, [operationType]);

    const showWarning = useCallback((message: string, type: 'error' | 'warning' = 'error') => {
        setWarningState({
            isVisible: true,
            message,
            type,
            operationType
        });

        // Auto-dismiss warnings after 10 seconds, but keep errors until manually dismissed
        if (type === 'warning') {
            setTimeout(() => {
                setWarningState(current => 
                    current?.type === 'warning' ? null : current
                );
            }, 10000);
        }
    }, [operationType]);

    const dismissWarning = useCallback(() => {
        setWarningState(null);
    }, []);

    const logViolation = useCallback((defaultPCCount: number) => {
        logDefaultPCSafetyViolation(operationType, userAlias, defaultPCCount, warehouseId);
    }, [operationType, userAlias, warehouseId]);

    return {
        warningState,
        validateSafety,
        showWarning,
        dismissWarning,
        logViolation
    };
}